import type { OnboardingCollectionResult, OnboardingFeeState } from '../api/customer-fees.api.js';

import { APP_LOCALE } from './locale.js';
import { formatTransactionAmount } from './transaction-money.js';

export interface CollectionMessage {
  readonly severity: 'success' | 'info' | 'warning' | 'error';
  readonly text: string;
}

/** What an operator should read after the platform tried to take the onboarding fee. */
export function describeOnboardingCollection(result: OnboardingCollectionResult): CollectionMessage {
  switch (result.outcome) {
    case 'COLLECTED':
      return {
        severity: 'success',
        text: `Onboarding fee collected: ${formatTransactionAmount(result.amountCents, result.currency)} taken from the client’s ${result.currency} account.`,
      };
    case 'ALREADY_COLLECTED':
      return {
        severity: 'info',
        text:
          result.amountCents !== undefined && result.currency !== undefined
            ? `The onboarding fee was already collected (${formatTransactionAmount(result.amountCents, result.currency)}). Nothing more was taken.`
            : 'The onboarding fee was already collected. Nothing more was taken.',
      };
    case 'INSUFFICIENT_FUNDS':
      return {
        severity: 'warning',
        text: `Onboarding fee not collected — not enough funds: available ${formatTransactionAmount(result.availableCents, result.currency)}, needed ${formatTransactionAmount(result.priceCents, result.currency)}. It will be taken in full once the money is there.`,
      };
    case 'NO_ACCOUNT':
      return {
        severity: 'warning',
        text: `Onboarding fee not collected — the client has no ${result.currency} account to take it from.`,
      };
    case 'NOT_ACTIVE':
      return {
        severity: 'warning',
        text: `Onboarding fee not collected — the client’s ${result.currency} account is not active.`,
      };
    case 'NO_PRICE':
      return {
        severity: 'info',
        text: `Onboarding fee not collected — there is no ${result.currency} onboarding price for this client.`,
      };
    case 'NOT_COLLECTED':
      return {
        severity: 'warning',
        text: 'Onboarding fee not collected. Core banking declined without a reason we could identify — check the account and try again.',
      };
    case 'ERROR':
      return {
        severity: 'error',
        text: 'The new price is saved, but collecting the onboarding fee failed. Try “Collect now” on the Onboarding fee row.',
      };
  }
}

/** "Collected 9 Oct 2026 · 1,480.00 EUR" or "Not collected"; null when unknown. */
export function onboardingChipLabel(state: OnboardingFeeState | null): string | null {
  if (state === null) return null;
  if (!state.collected) return 'Not collected';
  const when =
    state.at === undefined
      ? ''
      : ` ${new Date(state.at).toLocaleDateString(APP_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  const amount =
    state.amountCents !== undefined && state.currency !== undefined
      ? ` · ${formatTransactionAmount(state.amountCents, state.currency)}`
      : '';
  return `Collected${when}${amount}`;
}
